import { isValid, parse } from 'date-fns';
import {
  type ChangeEvent,
  type InputHTMLAttributes,
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import { useFormContext } from 'react-hook-form';

import { INPUT_DATE_PATTERN, INPUT_MONTH_PATTERN } from '../../../constants';
import { Input } from './styles';

type InputType = 'number' | 'password' | 'text';

export interface IFormInputTextProps
  extends InputHTMLAttributes<HTMLInputElement> {
  dataCy?: string;
  type: InputType;
  name: string;
  autoComplete?: string;
}

export const FormInputText = forwardRef<HTMLInputElement, IFormInputTextProps>(
  // eslint-disable-next-line no-restricted-syntax
  function FormInputText(
    {
      dataCy,
      type: inputType = 'text',
      name,
      autoComplete = 'off',
      onChange,
      ...inputProps
    },
    ref
  ): JSX.Element {
    const { register } = useFormContext();

    const { onChange: registerOnChange, ...registerProps } = register(name, {
      valueAsNumber: inputType === 'number',
    });

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      await registerOnChange(event);
    };

    return (
      <Input
        // @ts-expect-error: 'ref' is specified more than once, so this usage will be overwritten.
        ref={ref}
        {...inputProps}
        {...registerProps}
        onChange={(e) => {
          void handleChange(e);
        }}
        autoComplete={autoComplete}
        type={inputType}
        data-cy={dataCy}
      />
    );
  }
);

type InputDateType = 'date' | 'month';

export interface IFormInputDateProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  dataCy?: string;
  id: string;
  name: string;
  onValueChange?: (value: string) => void;
  type?: InputDateType;
  value?: string;
}

export const FormInputDate = forwardRef<HTMLInputElement, IFormInputDateProps>(
  // eslint-disable-next-line no-restricted-syntax
  function FormInputDate(
    { dataCy, name, id, value, onValueChange, type = 'date', ...inputProps },
    ref
  ): JSX.Element {
    const { register } = useFormContext();
    const valueDate = useMemo((): string => {
      return value ?? '';
    }, [value]);

    const isValidDate = (date: string): boolean => {
      const pattern =
        type === 'month' ? INPUT_MONTH_PATTERN : INPUT_DATE_PATTERN;
      try {
        const parsedDate = parse(date, pattern, new Date());
        return isValid(parsedDate);
      } catch (error) {
        return false;
      }
    };

    const handleValueDate = (date: string) => {
      onValueChange?.(date);
    };

    const handleBlurInputValueDate = (date: string) => {
      if (!isValidDate(date)) {
        handleValueDate('');
      }
    };

    const handleShowPicker = useCallback((htmlId: string) => {
      const element = document.getElementById(htmlId) as HTMLInputElement;
      if ('showPicker' in element) {
        element.showPicker();
      }
    }, []);

    return (
      <Input
        {...inputProps}
        {...register(name)}
        ref={ref}
        value={valueDate}
        id={id}
        name={name}
        onChange={(e) => {
          handleValueDate(e.target.value);
        }}
        type={type}
        onClick={() => {
          handleShowPicker(id);
        }}
        onBlur={(e) => {
          handleBlurInputValueDate(e.target.value);
        }}
        data-cy={dataCy}
      />
    );
  }
);
